import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  sub: string;
  role: 'admin' | 'facility';
  facilityId?: string;
  iat?: number;
  exp?: number;
  iss?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthActions {
  login: (token: string, user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: Partial<User>) => void;
  isTokenExpired: () => boolean;
  hasRole: (role: string | string[]) => boolean;
  canAccessFacility: (facilityId: string) => boolean;
}

type AuthStore = AuthState & AuthActions;

/**
 * Decode JWT token payload (without verification)
 */
function decodeToken(token: string): User | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${  (`00${  c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    
    return JSON.parse(jsonPayload) as User;
  } catch {
    // Silent fail for token decoding errors
    return null;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired(token: string): boolean {
  try {
    const user = decodeToken(token);
    if (!user || !user.exp) return true;
    
    const now = Math.floor(Date.now() / 1000);
    return user.exp < now;
  } catch {
    return true;
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      // Actions
      login: (token: string, user: User) => {
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },

      isTokenExpired: () => {
        const { token } = get();
        if (!token) return true;
        return isTokenExpired(token);
      },

      hasRole: (role: string | string[]) => {
        const { user } = get();
        if (!user) return false;
        
        const roles = Array.isArray(role) ? role : [role];
        return roles.includes(user.role);
      },

      canAccessFacility: (facilityId: string) => {
        const { user } = get();
        if (!user) return false;
        
        // Admin can access all facilities
        if (user.role === 'admin') return true;
        
        // Facility users can only access their own facility
        if (user.role === 'facility') {
          return user.facilityId === facilityId;
        }
        
        return false;
      },
    }),
    {
      name: 'csam-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token && isTokenExpired(state.token)) {
          // Token is expired, clear auth state
          state.token = null;
          state.user = null;
          state.isAuthenticated = false;
        }
      },
    }
  )
);

// Selectors for easier access to specific state
export const useAuth = () => {
  const store = useAuthStore();
  return {
    token: store.token,
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
  };
};

export const useAuthActions = () => {
  const store = useAuthStore();
  return {
    login: store.login,
    logout: store.logout,
    setLoading: store.setLoading,
    updateUser: store.updateUser,
  };
};

export const useAuthChecks = () => {
  const store = useAuthStore();
  return {
    isTokenExpired: store.isTokenExpired,
    hasRole: store.hasRole,
    canAccessFacility: store.canAccessFacility,
  };
};

// Helper hooks for common use cases
export const useIsAdmin = () => {
  return useAuthStore(state => state.user?.role === 'admin');
};

export const useIsFacility = () => {
  return useAuthStore(state => state.user?.role === 'facility');
};

export const useCurrentFacilityId = () => {
  return useAuthStore(state => state.user?.facilityId);
};

export const useUserId = () => {
  return useAuthStore(state => state.user?.sub);
};
